import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, ApiError } from "../lib/api";
import { useAppConfig } from "../lib/appConfig";
import { suggestEmails } from "../lib/emailDomains";
import { useI18n } from "../lib/i18n";
import { useSession } from "../lib/session";
import { Field, Notice, PageHeader } from "../ui/kit";

type Method = "email" | "phone";

export function SignIn() {
  const { t } = useI18n();
  const { refresh } = useSession();
  const { phoneSignIn } = useAppConfig();
  const nav = useNavigate();
  const [params] = useSearchParams();
  // Only a path on this site: "//host" and "/\host" would leave it.
  const wanted = params.get("next") ?? "";
  const next = /^\/(?![/\\])/.test(wanted) ? wanted : "/";
  // E-mail is the default way to register and sign in; a mobile number still works.
  const [method, setMethod] = useState<Method>("email");
  const [contact, setContact] = useState("");
  const [challenge, setChallenge] = useState<{ challengeId: string; sentTo: string; devCode?: string } | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const isEmail = method === "email";
  const base = isEmail ? "/api/auth/email" : "/api/auth/otp";
  const suggestions = isEmail ? suggestEmails(contact) : [];
  const contactBody = () => (isEmail ? { email: contact } : { phone: contact });

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      setChallenge(await api(`${base}/send`, { body: contactBody() }));
      setCode("");
    } catch (e) {
      setError((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    setError(null);
    try {
      await api(`${base}/verify`, { body: { challengeId: challenge!.challengeId, ...contactBody(), code } });
      await refresh();
      nav(next, { replace: true });
    } catch (e) {
      setError((e as ApiError).message);
    } finally {
      setBusy(false);
    }
  };

  const switchMethod = () => {
    setMethod(isEmail ? "phone" : "email");
    setContact("");
    setChallenge(null);
    setError(null);
  };

  return (
    <div style={{ maxWidth: 440, margin: "0 auto" }}>
      <PageHeader title={t(isEmail ? "signin.emailTitle" : "signin.title")} back />
      <p className="muted">{t(isEmail ? "signin.emailWhy" : "signin.why")}</p>
      <form
        className="card stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (challenge) verify();
          else send();
        }}
      >
        {!challenge ? (
          <>
            {isEmail ? (
              <>
                <Field label={t("signin.email")} hint={t("signin.emailHint")} error={error}>
                  {(id, d) => (
                    <input
                      id={id}
                      className="input ltr"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      autoCapitalize="none"
                      spellCheck={false}
                      value={contact}
                      onChange={(e) => setContact(e.target.value)}
                      onKeyDown={(e) => {
                        // Tab completes the domain ("name@g" → name@gmail.com), like a search box.
                        if (e.key === "Tab" && !e.shiftKey && suggestions[0]?.kind === "complete") {
                          e.preventDefault();
                          setContact(suggestions[0].email);
                        }
                      }}
                      aria-describedby={d}
                      aria-invalid={!!error || undefined}
                      required
                    />
                  )}
                </Field>
                {suggestions[0]?.kind === "complete" && (
                  <div className="chips ltr" role="group" aria-label={t("signin.suggestions")}>
                    {suggestions.map((s) => (
                      <button key={s.email} type="button" className="chip" onClick={() => setContact(s.email)}>
                        @{s.email.split("@")[1]}
                      </button>
                    ))}
                  </div>
                )}
                {suggestions[0]?.kind === "typo" && (
                  <p className="muted" style={{ margin: 0 }} aria-live="polite">
                    {t("signin.didYouMean")}{" "}
                    <button type="button" className="btn ghost sm ltr" onClick={() => setContact(suggestions[0]!.email)}>
                      {suggestions[0].email}
                    </button>
                  </p>
                )}
              </>
            ) : (
              <Field label={t("signin.phone")} hint={t("signin.phoneHint")} error={error}>
                {(id, d) => (
                  <input id={id} className="input ltr" type="tel" inputMode="tel" autoComplete="tel" value={contact} onChange={(e) => setContact(e.target.value)} aria-describedby={d} aria-invalid={!!error || undefined} required />
                )}
              </Field>
            )}
            <button className="btn primary block" disabled={busy || (isEmail ? !/^\S+@\S+\.\S+$/.test(contact.trim()) : contact.length < 6)}>{t("signin.sendCode")}</button>
            {(phoneSignIn || !isEmail) && (
              <button type="button" className="btn ghost sm" onClick={switchMethod}>{t(isEmail ? "signin.usePhone" : "signin.useEmail")}</button>
            )}
          </>
        ) : (
          <>
            {/* Show the full address the person typed, so a typo ("walashy@" for "walashyksa@") is easy to spot. */}
            <Field label={t("signin.code")} hint={t("signin.sentTo", { p: isEmail ? contact.trim().toLowerCase() : challenge.sentTo })} error={error}>
              {(id, d) => (
                <input
                  id={id}
                  className="input otp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/[٠-٩]/g, (x) => String(x.charCodeAt(0) - 0x0660)).replace(/\D/g, ""))}
                  aria-describedby={d}
                  aria-invalid={!!error || undefined}
                  autoFocus
                />
              )}
            </Field>
            {challenge.devCode && <Notice kind="warn">{t("signin.devCode", { c: challenge.devCode })}</Notice>}
            {isEmail && !challenge.devCode && <p className="muted" style={{ margin: 0 }}>{t("signin.checkSpam")}</p>}
            <button className="btn primary block" disabled={busy || code.length !== 6}>{t("signin.verify")}</button>
            <div className="row between">
              <button type="button" className="btn ghost sm" onClick={() => setChallenge(null)}>{t(isEmail ? "signin.changeEmail" : "signin.changeNumber")}</button>
              <button type="button" className="btn ghost sm" onClick={send} disabled={busy}>{t("signin.resend")}</button>
            </div>
          </>
        )}
      </form>
      <p className="small muted" style={{ marginTop: "var(--s4)" }}>
        {t("signin.agree")} <Link to="/terms">{t("info.terms")}</Link> · <Link to="/privacy">{t("info.privacy")}</Link>
      </p>
    </div>
  );
}
